"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, Sparkles, CircleCheck as CheckCircle2, PhoneCall, MapPin, Search } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqsData = [{
  id: "item-1",
  question: "Why should a Telford business focus specifically on Local SEO?",
  answer: "Telford and Shropshire have a rapidly growing local economy. When local customers search for your services on Google, they rely on Map Pack results and location-targeted search results. Focusing on Telford local SEO ensures your business captures high-intent traffic right at the moment potential clients are ready to buy, rather than losing leads to regional or national competitors."
}, {
  id: "item-2",
  question: "Will I work directly with you, Yassine, or an account manager?",
  answer: "You will work 100% directly with me. I am an independent freelance SEO consultant, not an agency. When you hire YE Consultancy, there are no junior account managers or outsourced hand-offs. I personally handle your local audit, strategy, Google Business Profile optimization, technical fixes, and monthly growth execution."
}, {
  id: "item-3",
  question: "How long does it take to see results in Telford local search results?",
  answer: "Most local Telford campaigns show initial traction in Google Map Pack visibility and localized queries within 60 to 90 days. Competitive sectors might require 4 to 6 months of sustained authority building, technical optimization, and review management to reach top 3 local rankings consistently."
}, {
  id: "item-4",
  question: "What is included in your Telford Google Business Profile optimization?",
  answer: "My GBP service includes deep category auditing, primary and secondary keyword optimization, primary service mapping, geo-tagged asset optimization, citation cleanup, review response frameworks, and anti-spam monitoring against competitors who violate Google guidelines in the Telford area."
}, {
  id: "item-5",
  question: "Can local SEO help if my business serves Telford and surrounding areas like Shrewsbury?",
  answer: "Yes, absolutely. I design dedicated local service area strategies that establish relevance across key Shropshire locations including Telford, Shrewsbury, Wellington, Newport, and Shifnal. We structure location pages and schema markup so search engines understand your true service radius."
}, {
  id: "item-6",
  question: "How do you build authentic local citations and authority for Telford companies?",
  answer: "I focus on high-quality, relevant UK directories, local business associations, regional press opportunities, and industry-specific hubs. I never use automated citation blasts or low-quality link farms, as these can trigger Google spam penalties and damage your domain's trust."
}, {
  id: "item-7",
  question: "What makes your independent consultancy different from a Telford marketing agency?",
  answer: "Agencies often lock you into rigid retainer contracts where your project gets passed to junior staff with high employee turnover. As an independent consultant, I offer direct access, customized strategies tailored to your exact business goals, total transparency, and rapid implementation without corporate overhead."
}, {
  id: "item-8",
  question: "How do you track and report local search performance?",
  answer: "I provide clear, fluff-free monthly reporting. We track geo-grid rankings (how you rank across different neighborhoods in Telford), organic phone calls, Google Maps directions, contact form submissions, and overall qualified organic traffic growth."
}, {
  id: "item-9",
  question: "Do I need a complete website redesign for local SEO to work?",
  answer: "Not necessarily. In most cases, I optimize your existing website by improving on-page content, internal link structure, page load speed, mobile usability, and local schema markup. I only recommend structural changes or redesigns if your current site severely hinders crawlability or mobile conversion."
}, {
  id: "item-10",
  question: "How do we get started with a Telford Local SEO audit?",
  answer: "The first step is booking a free consultation directly with me. I will review your current local visibility, analyze your key Telford competitors, and map out a clear action plan during our conversation."
}];
const highlights = [{
  title: "100% Direct Consultant Access",
  desc: "No middlemen, account executives, or junior staff."
}, {
  title: "Data-Backed Shropshire Insights",
  desc: "Targeted keyword research mapped to Telford buyer intent."
}, {
  title: "Transparent Monthly Reporting",
  desc: "Track real phone calls, leads, and localized Map rankings."
}];
export default function Telfordfaq() {
  const shouldReduceMotion = useReducedMotion();
  const [searchTerm, setSearchTerm] = useState("");
  const filteredFaqs = faqsData.filter(faq => faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(searchTerm.toLowerCase()));
  return <section id="telfordlocalseofaq" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-3 py-1 gap-1.5 text-xs font-semibold rounded-full border border-border">
              <Sparkles className="size-3.5 text-primary" />
              <span>Telford Local Search Expertise</span>
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-4">
            Frequently Asked Questions About <span className="text-primary">Local SEO in Telford</span>
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Get straight, honest answers from Yassine Essaadi. Learn how my hands-on local search strategies help your Shropshire business outrank local competitors and generate high-converting leads.
          </motion.p>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="mt-8 max-w-md mx-auto relative">
            <div className="relative flex items-center">
              <Search className="absolute left-3.5 size-4 text-muted-foreground pointer-events-none" />
              <input type="text" placeholder="Search Telford SEO questions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all shadow-xs" />
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-md relative overflow-hidden">
              <div className="flex items-center gap-3 mb-6">
                <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-lg">Direct Consultant Access</h3>
                  <p className="text-xs text-muted-foreground">Telford & Shropshire Focus</p>
                </div>
              </div>

              <p className="text-sm text-foreground/90 leading-relaxed mb-6">
                {"Have a specific question about your Telford business rankings? You will never be passed to an account manager. Work directly with me to build a sustainable local search pipeline."}
              </p>

              <div className="space-y-3 pt-2 border-t border-border/60 mb-6">
                {highlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>)}
              </div>

              <div className="space-y-3">
                <Button asChild size="lg" className="w-full font-medium shadow-sm">
                  <Link to="/ContactPage" className="flex items-center justify-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Book a Free Consultation</span>
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full font-medium border-border">
                  <Link to="/CaseStudiesPage" className="flex items-center justify-center gap-2">
                    <span>Explore Case Studies</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="bg-muted/60 border border-border rounded-xl p-5 text-center">
              <MessageSquare className="size-5 text-primary mx-auto mb-2" />
              <p className="text-xs font-semibold text-foreground mb-1">Prefer an email answer?</p>
              <p className="text-xs text-muted-foreground mb-3">Reach out directly to Yassine</p>
              <a href="mailto:contact@ye-consultancy.com" className="text-xs font-medium text-primary hover:underline">
                contact@ye-consultancy.com
              </a>
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-8">
            {filteredFaqs.length === 0 ? <div className="bg-card border border-border rounded-2xl p-8 text-center my-6">
                <HelpCircle className="size-8 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-1">No matching questions found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {"Try searching with a different term or feel free to contact me directly."}
                </p>
                <Button variant="outline" size="sm" onClick={() => setSearchTerm("")}>
                  Clear search
                </Button>
              </div> : <Accordion type="single" collapsible defaultValue="item-1" className="space-y-3">
                {filteredFaqs.map((faq, i) => <div key={faq.id} data-index={i}>
                    <AccordionItem value={faq.id} className="bg-card border border-border rounded-xl px-4 sm:px-6 py-1 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                      <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline text-base sm:text-lg py-4 group">
                        <span className="pr-2">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-foreground/90 font-sans text-sm sm:text-base leading-relaxed pt-1 pb-5 border-t border-border/40 mt-1">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </div>)}
              </Accordion>}

            <div className="mt-8 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="space-y-1 text-center sm:text-left">
                <h4 className="font-semibold font-serif text-lg text-foreground">
                  Ready to grow your Telford search rankings?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Get a tailored local SEO strategy designed specifically for your target audience.
                </p>
              </div>
              <Button asChild size="default" className="shrink-0">
                <Link to="/LocalSEOPage" className="flex items-center gap-2">
                  <span>Explore Telford Local SEO</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}